"use client"

import { motion, useMotionTemplate, useMotionValue, AnimatePresence } from "framer-motion"
import { ExternalLink, Github, Eye } from "lucide-react"
import Image from "next/image"
import { useState } from "react"
import { ProjectFilter } from "./project-filter"
import { ProjectModal } from "./project-modal"
import { SectionDivider } from "./section-divider"

const projects = [
    {
        title: "LofiHaven",
        description: "A full-featured lofi music platform with focus sessions, user profiles, gamification, and Discord bot integration.",
        image: "/lofihaven.png",
        tags: ["Next.js", "Supabase", "TypeScript", "Tailwind CSS"],
        category: "Active",
        links: { demo: "https://lofihaven.xyz", github: "" },
        color: "from-purple-500/20 to-pink-500/20",
        caseStudy: {
            problem: "Wanted to create a relaxing environment for focused work with ambient music and community features.",
            solution: "Built an immersive platform with curated lofi streams, focus sessions, achievement system, and a collaborative lobby with real-time presence.",
            outcome: "My first real project—still actively developing and learning from it every day!",
        }
    },
    {
        title: "Akira Bot",
        description: "A powerful multi-purpose Discord bot with over 400 commands covering moderation, fun, utility, and more.",
        image: "/akira.png",
        tags: ["JavaScript", "Discord.js", "Node.js"],
        category: "Discontinued",
        links: { demo: "", github: "" },
        color: "from-blue-500/20 to-cyan-500/20",
        caseStudy: {
            problem: "Wanted to learn programming by building something practical and interactive.",
            solution: "Created a feature-rich Discord bot from scratch, learning JavaScript fundamentals and API interactions along the way.",
            outcome: "A major milestone in my coding journey. Taught me project structure, command handling, and database integration.",
        }
    },
    {
        title: "Aether",
        description: "A Discord ticket bot system with a web dashboard for server management and support ticket handling.",
        image: "/aether.png",
        tags: ["JavaScript", "Discord.js", "Web Dashboard"],
        category: "Discontinued",
        links: { demo: "", github: "" },
        color: "from-green-500/20 to-emerald-500/20",
        caseStudy: {
            problem: "Servers needed an easy way to manage support tickets with a visual interface.",
            solution: "Built a ticket system bot with a companion web dashboard for managing tickets and viewing statistics.",
            outcome: "Learned about web development and how to connect bots with web interfaces.",
        }
    }
]

const categories = ["All", "Active", "Discontinued"]

export function UltraProjects() {
    const [activeCategory, setActiveCategory] = useState("All")
    const [selectedProject, setSelectedProject] = useState<typeof projects[0] | null>(null)

    const filteredProjects = activeCategory === "All"
        ? projects
        : projects.filter((p) => p.category === activeCategory)

    return (
        <section id="projects" className="py-32 px-4 relative">
            <SectionDivider variant="dots" className="mb-16" />

            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-12"
                >
                    <div className="text-center space-y-4 mb-12">
                        <span className="inline-block text-sm font-medium px-4 py-1.5 rounded-full bg-muted/10 border border-border text-foreground backdrop-blur-sm">
                            Featured Work
                        </span>
                        <h2 className="text-4xl md:text-6xl font-bold">Selected Projects</h2>
                        <div className="h-1 w-20 bg-primary rounded-full mx-auto" />
                    </div>
                </motion.div>

                {/* Filter */}
                <ProjectFilter
                    categories={categories}
                    activeCategory={activeCategory}
                    onCategoryChange={setActiveCategory}
                />

                {/* Projects grid */}
                <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <AnimatePresence mode="popLayout">
                        {filteredProjects.map((project, i) => (
                            <ProjectCard
                                key={project.title}
                                project={project}
                                index={i}
                                onViewDetails={() => setSelectedProject(project)}
                            />
                        ))}
                    </AnimatePresence>
                </motion.div>

                {/* View all button */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mt-16"
                >
                    <a
                        href="https://github.com/kevin-b-official"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-border bg-muted/10 hover:bg-primary/10 hover:border-primary/50 transition-all"
                    >
                        <Github className="w-5 h-5" />
                        <span>View All on GitHub</span>
                    </a>
                </motion.div>
            </div>

            {/* Project Modal */}
            <ProjectModal
                project={selectedProject}
                isOpen={!!selectedProject}
                onClose={() => setSelectedProject(null)}
            />
        </section>
    )
}

function ProjectCard({ project, index, onViewDetails }: { project: typeof projects[0]; index: number; onViewDetails: () => void }) {
    const mouseX = useMotionValue(0)
    const mouseY = useMotionValue(0)

    function handleMouseMove({ currentTarget, clientX, clientY }: React.MouseEvent) {
        const { left, top } = currentTarget.getBoundingClientRect()
        mouseX.set(clientX - left)
        mouseY.set(clientY - top)
    }

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ delay: index * 0.1 }}
            onMouseMove={handleMouseMove}
            className="group relative rounded-3xl bg-card border border-border overflow-hidden"
        >
            <motion.div
                className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 transition duration-300 group-hover:opacity-100"
                style={{
                    background: useMotionTemplate`
            radial-gradient(
              650px circle at ${mouseX}px ${mouseY}px,
              hsl(var(--foreground) / 0.1),
              transparent 80%
            )
          `,
                }}
            />

            <div className="relative h-full flex flex-col">
                <div className="aspect-video relative overflow-hidden bg-muted">
                    <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-50`} />
                    {project.image && (
                        <Image
                            src={project.image}
                            alt={project.title}
                            fill
                            className="object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                    )}

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <motion.button
                            initial={{ scale: 0.8, opacity: 0 }}
                            whileHover={{ scale: 1.1 }}
                            onClick={onViewDetails}
                            className="px-6 py-3 bg-primary text-primary-foreground rounded-full font-medium flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300"
                        >
                            <Eye className="w-4 h-4" />
                            View Details
                        </motion.button>
                    </div>
                </div>

                <div className="p-8 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-2xl font-bold">{project.title}</h3>
                        <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                            {project.category}
                        </span>
                    </div>
                    <p className="text-muted-foreground mb-6 flex-1">{project.description}</p>

                    <div className="flex flex-wrap gap-2 mb-6">
                        {project.tags.map((tag: string) => (
                            <span key={tag} className="px-3 py-1 rounded-full text-xs font-medium bg-muted/10 border border-border">
                                {tag}
                            </span>
                        ))}
                    </div>

                    <div className="flex gap-4">
                        {project.links.demo && (
                            <a
                                href={project.links.demo}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Live Demo
                            </a>
                        )}
                        {project.links.github && project.links.github !== "#" && (
                            <a
                                href={project.links.github}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
                            >
                                <Github className="w-4 h-4" />
                                Source Code
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    )
}
